import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid, 
  Typography,
  Divider,
  Slider,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Collapse
} from '@mui/material';
import { 
  LocalGasStation, 
  Speed, 
  CalendarToday, 
  Euro,
  Clear,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { CarFront, Repeat } from 'lucide-react';

const SearchFilters = ({
  filters,
  filterOptions,
  handleFilterChange,
  darkMode,
  isFullscreen,
  isMobile
}) => {
  const [expanded, setExpanded] = useState(!isMobile);
  
  const priceRanges = [
    { label: 'Up to €10,000', min: 0, max: 10000 },
    { label: '€10,000 - €20,000', min: 10000, max: 20000 },
    { label: '€20,000 - €30,000', min: 20000, max: 30000 },
    { label: '€30,000 - €50,000', min: 30000, max: 50000 },
    { label: '€50,000 - €100,000', min: 50000, max: 100000 },
    { label: 'Over €100,000', min: 100000, max: 1000000 },
  ];

  const yearMarks = [
    { value: filterOptions.year_range?.min_year || 1990, label: (filterOptions.year_range?.min_year || 1990).toString() },
    { value: filterOptions.year_range?.max_year || new Date().getFullYear(), label: (filterOptions.year_range?.max_year || new Date().getFullYear()).toString() },
  ];

  const resetAllFilters = () => {
    Object.keys(filters).forEach(key => {
      handleFilterChange(key, '');
    });
  };

  const resetFilter = (filterName) => {
    handleFilterChange(filterName, '');
  };

  // Count active filters
  const activeFiltersCount = Object.values(filters).filter(val => val !== '').length;

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper sx={{ 
      p: 2.5, 
      mb: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      borderRadius: 2,
      bgcolor: darkMode ? '#2A2A2A' : '#fff',
      ...(isMobile && {
        position: 'relative',
        zIndex: 1200,
        transition: 'max-height 0.3s ease',
        overflow: expanded ? 'visible' : 'hidden'
      })
    }}>
      {/* Filter Header */}
      <Box sx={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: expanded ? 2 : 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CarFront size={18} />
            Car Filters
            {activeFiltersCount > 0 && (
              <Chip 
                label={activeFiltersCount} 
                size="small" 
                color="primary" 
                sx={{ 
                  height: 20, 
                  minWidth: 20,
                  fontSize: '0.75rem',
                }} 
              />
            )}
          </Typography>
          
          {isMobile && activeFiltersCount > 0 && (
            <Chip 
              label={`${activeFiltersCount} active`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeFiltersCount > 0 && (
            <Button 
              size="small" 
              variant="text" 
              startIcon={<Repeat size={14} />} 
              onClick={resetAllFilters}
              sx={{ 
                textTransform: 'none',
                color: darkMode ? '#bbb' : 'text.secondary'
              }}
            >
              Reset
            </Button>
          )}
          
          {isMobile && (
            <IconButton 
              size="small" 
              onClick={toggleExpanded}
              sx={{ 
                ml: 1,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }}
            >
              <ExpandMore />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto">
        <Grid container spacing={2.5} sx={{ mt: isMobile ? 1 : 0 }}>
          {/* Basic Filters */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>Brand</InputLabel>
              <Select
                value={filters.brand}
                onChange={(e) => handleFilterChange('brand', e.target.value)}
                label="Brand"
                startAdornment={
                  filters.brand && (
                    <Tooltip title="Clear">
                      <IconButton 
                        size="small" 
                        sx={{ mr: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          resetFilter('brand');
                        }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
              >
                <MenuItem value="">All Brands</MenuItem>
                {filterOptions.brands.map((brand) => (
                  <MenuItem key={brand} value={brand}>{brand}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fuel Type Filter */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Fuel Type</InputLabel>
              <Select
                value={filters.fuel}
                onChange={(e) => handleFilterChange('fuel', e.target.value)}
                label="Fuel Type"
                startAdornment={
                  filters.fuel && (
                    <Tooltip title="Clear">
                      <IconButton 
                        size="small" 
                        sx={{ mr: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          resetFilter('fuel');
                        }}
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )
                }
                IconComponent={() => <LocalGasStation sx={{ mr: 1, color: '#4361EE' }} />}
              >
                <MenuItem value="">All Fuel Types</MenuItem>
                {filterOptions.fuel_types.map((fuel) => (
                  <MenuItem key={fuel} value={fuel}>{fuel}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Divider */}
          <Grid item xs={12}>
            <Divider sx={{ mt: 1, mb: 2 }} />
          </Grid>

          {/* Price Range Filter */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ 
              mb: 2,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Euro sx={{ color: '#4361EE' }} />
              Price Range
              {(filters.min_price || filters.max_price) && (
                <Tooltip title="Clear price range">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      resetFilter('min_price');
                      resetFilter('max_price');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            <Box sx={{ px: 1 }}>
              <Grid container spacing={1.5}>
                {priceRanges.map((range) => (
                  <Grid item xs={6} sm={4} key={range.label}>
                    <Chip
                      label={range.label}
                      onClick={() => {
                        handleFilterChange('min_price', range.min);
                        handleFilterChange('max_price', range.max);
                      }}
                      variant={
                        filters.min_price === range.min && 
                        filters.max_price === range.max
                          ? "filled" 
                          : "outlined"
                      }
                      color={
                        filters.min_price === range.min && 
                        filters.max_price === range.max
                          ? "primary" 
                          : "default"
                      }
                      sx={{ 
                        width: '100%',
                        py: 1.5,
                        borderRadius: 1.5,
                        fontWeight: 500,
                        height: 'auto',
                        '&:hover': {
                          bgcolor: darkMode 
                            ? filters.min_price === range.min && filters.max_price === range.max
                              ? 'primary.dark'
                              : 'rgba(255,255,255,0.1)'
                            : filters.min_price === range.min && filters.max_price === range.max
                              ? 'primary.light'
                              : 'rgba(0,0,0,0.08)'
                        }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>

          {/* Year Range Filter */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ 
              mt: 1,
              mb: 2,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <CalendarToday sx={{ color: '#4361EE' }} />
              Year Range
              {(filters.min_year || filters.max_year) && (
                <Tooltip title="Clear year range">
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      resetFilter('min_year');
                      resetFilter('max_year');
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              px: 2,
              mb: 1
            }}>
              <FormControl size="small">
                <Select
                  value={filters.min_year}
                  onChange={(e) => handleFilterChange('min_year', e.target.value)}
                  displayEmpty
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="">Min</MenuItem>
                  {Array.from(
                    { length: filterOptions.year_range?.max_year - filterOptions.year_range?.min_year + 1 },
                    (_, i) => filterOptions.year_range?.min_year + i
                  ).map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="body2" color="text.secondary">to</Typography>
              
              <FormControl size="small">
                <Select
                  value={filters.max_year}
                  onChange={(e) => handleFilterChange('max_year', e.target.value)}
                  displayEmpty
                  sx={{ minWidth: 100 }}
                >
                  <MenuItem value="">Max</MenuItem>
                  {Array.from(
                    { length: filterOptions.year_range?.max_year - filterOptions.year_range?.min_year + 1 },
                    (_, i) => filterOptions.year_range?.min_year + i
                  ).map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </Collapse>
    </Paper>
  );
};

export default SearchFilters; 